import type { GroupParticipant } from '@packages/contracts/group';
import { exists } from '@packages/primitives/exists';
import { Avatar, AvatarFallback } from '@packages/ui/base/avatar/avatar';
import { Card } from '@packages/ui/base/card/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@packages/ui/base/collapsible/collapsible';
import { CircularProgress } from '@packages/ui/base/progress/circular_progress';
import { CaretDownIcon, CaretUpIcon, CheckCircleIcon, ClockIcon } from '@phosphor-icons/react';
import { useState } from 'react';

export interface ProgressCardProps {
  participants: Pick<GroupParticipant, 'id' | 'name' | 'matchViewedAt'>[];
  currentParticipantId: string;
}

/** Shows how many participants have viewed their match, according to the database. */
export const ProgressCard = ({ participants, currentParticipantId }: ProgressCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const totalParticipants = participants.length;
  const numberOfDrawn = participants.filter(isDrawn).length;
  const progressPercent = totalParticipants > 0 ? (numberOfDrawn / totalParticipants) * 100 : 0;

  const getDisplayName = (participant: Pick<GroupParticipant, 'id' | 'name'>) =>
    participant.id === currentParticipantId ? `${participant.name} (you)` : participant.name;

  // Always show Waiting (pending) at the top, Drawn at the bottom
  const sortedParticipants = [...participants].sort(
    (a, b) =>
      Number(isDrawn(a)) - Number(isDrawn(b)) || getDisplayName(a).localeCompare(getDisplayName(b)),
  );

  return (
    <Card className="overflow-hidden px-0 py-0 md:px-0">
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between px-6 py-5 text-left transition-colors hover:bg-zinc-50 md:px-8"
          >
            <div className="flex items-center gap-3">
              <CircularProgress value={progressPercent} size={32} className="shrink-0" />
              <p className="text-lg font-bold">
                {numberOfDrawn} of {totalParticipants} participants drew
              </p>
            </div>
            {isOpen ? (
              <CaretUpIcon weight="bold" className="size-5 shrink-0 text-zinc-600" />
            ) : (
              <CaretDownIcon weight="bold" className="size-5 shrink-0 text-zinc-600" />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="flex flex-col items-center gap-3 px-6 pt-1 pb-6 md:px-8">
            {sortedParticipants.map(participant => (
              <div key={participant.id} className="flex w-full items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar className="size-7 shrink-0">
                    <AvatarFallback
                      name={participant.name}
                      className="bg-zinc-100 text-[10px] font-bold text-zinc-600"
                    />
                  </Avatar>
                  <span className="truncate text-sm font-medium text-zinc-700">
                    {getDisplayName(participant)}
                  </span>
                </div>

                <div className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
                  {isDrawn(participant) ? (
                    <>
                      <span className="text-primary hidden sm:inline">Drawn</span>
                      <CheckCircleIcon
                        className="text-primary size-5"
                        weight="fill"
                        aria-label="Drawn"
                      />
                    </>
                  ) : (
                    <>
                      <span className="hidden text-zinc-400 sm:inline">Waiting</span>
                      <ClockIcon
                        className="size-5 text-zinc-300"
                        weight="bold"
                        aria-label="Waiting"
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

function isDrawn(participant: Pick<GroupParticipant, 'matchViewedAt'>): boolean {
  return exists(participant.matchViewedAt);
}
